import { useState, useEffect, useMemo } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Calendar, Clock, MapPin, Plus, CheckCircle, XCircle, AlertTriangle,
  Truck, Wrench, Shield, FileText, Search, ChevronLeft, ChevronRight,
  User, Phone, Bell, Loader2, Trash2
} from "lucide-react";

const TYPE_CONFIG = {
  rar: { label: "RAR / ITP", icon: Shield, color: "bg-red-500", alertDays: 3, desc: "Inspectie tehnica periodica (2 ani)" },
  revizie: { label: "Revizie", icon: Wrench, color: "bg-blue-500", alertDays: 1, desc: "Revizie programata client" },
  reparatie: { label: "Reparatie", icon: FileText, color: "bg-orange-500", alertDays: 1, desc: "Reparatie programata" },
  livrare: { label: "Livrare / Ridicare", icon: Truck, color: "bg-green-500", alertDays: 1, desc: "Livrare la client sau ridicare" },
};

const STATUS_CONFIG = {
  programat: { label: "Programat", color: "bg-yellow-500 text-black" },
  verificat: { label: "Verificat", color: "bg-blue-500 text-white" },
  finalizat: { label: "Finalizat", color: "bg-green-500 text-white" },
  anulat: { label: "Anulat", color: "bg-zinc-500 text-white" },
};

function getDaysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  const target = new Date(dateStr); target.setHours(0,0,0,0);
  return Math.ceil((target - today) / 86400000);
}

function CalendarView({ programari, currentMonth, setCurrentMonth }) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startDay = firstDay === 0 ? 6 : firstDay - 1;

  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const monthNames = ["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"];

  const getEventsForDay = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return programari.filter(p => p.scheduled_date === dateStr);
  };

  const today = new Date();
  const isToday = (day) => day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden" data-testid="calendar-view">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="font-bold text-sm">{monthNames[month]} {year}</span>
        <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 text-center text-xs text-muted-foreground border-b border-border">
        {["Lu","Ma","Mi","Jo","Vi","Sa","Du"].map(d => <div key={d} className="py-2 font-medium">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const events = getEventsForDay(day);
          return (
            <div key={i} className={`min-h-[70px] p-1 border-r border-b border-border/50 ${!day ? 'bg-secondary/20' : ''} ${isToday(day) ? 'bg-primary/10' : ''}`}>
              {day && (
                <>
                  <span className={`text-xs font-mono ${isToday(day) ? 'font-bold text-primary' : 'text-muted-foreground'}`}>{day}</span>
                  <div className="space-y-0.5 mt-0.5">
                    {events.slice(0, 3).map(ev => {
                      const cfg = TYPE_CONFIG[ev.programare_type] || TYPE_CONFIG.revizie;
                      return (
                        <div key={ev.programare_id} className={`${cfg.color} text-white text-[8px] rounded px-1 py-0.5 truncate leading-tight`} title={`${cfg.label}: ${ev.scooter_label}`}>
                          {ev.scheduled_time ? ev.scheduled_time + ' ' : ''}{cfg.label.split(' ')[0]}
                        </div>
                      );
                    })}
                    {events.length > 3 && <div className="text-[8px] text-muted-foreground">+{events.length - 3}</div>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CreateProgramareDialog({ scooters, onCreated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ scooter_id: "", type: "rar", scheduled_date: "", scheduled_time: "", location: "", client_name: "", client_phone: "", notes: "" });

  const handleSubmit = async () => {
    if (!form.scooter_id || !form.scheduled_date) {
      toast.error("Selecteaza scuterul si data"); return;
    }
    setLoading(true);
    try {
      await api.post("/programari", form);
      toast.success("Programare creata!");
      setOpen(false);
      setForm({ scooter_id: "", type: "rar", scheduled_date: "", scheduled_time: "", location: "", client_name: "", client_phone: "", notes: "" });
      onCreated();
    } catch (err) {
      toast.error("Eroare la creare");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="create-programare-btn"><Plus className="w-4 h-4 mr-2" /> Programare noua</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Programare Noua</DialogTitle></DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Scooter *</Label>
            <Select value={form.scooter_id} onValueChange={v => setForm(p => ({ ...p, scooter_id: v }))}>
              <SelectTrigger data-testid="select-scooter"><SelectValue placeholder="Selecteaza scuterul" /></SelectTrigger>
              <SelectContent>
                {scooters.map(s => (
                  <SelectItem key={s.scooter_id} value={s.scooter_id}>{s.brand} {s.model} ({s.scooter_id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Tip programare *</Label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button key={key} type="button" onClick={() => setForm(p => ({ ...p, type: key }))}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-sm ${form.type === key ? 'border-primary bg-primary/10 font-medium' : 'border-border hover:border-primary/50'}`}
                    data-testid={`type-${key}`}>
                    <div className={`w-6 h-6 rounded ${cfg.color} flex items-center justify-center`}><Icon className="w-3 h-3 text-white" /></div>
                    {cfg.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input type="date" value={form.scheduled_date} onChange={e => setForm(p => ({ ...p, scheduled_date: e.target.value }))} data-testid="input-date" />
            </div>
            <div className="space-y-2">
              <Label>Ora</Label>
              <Input type="time" value={form.scheduled_time} onChange={e => setForm(p => ({ ...p, scheduled_time: e.target.value }))} data-testid="input-time" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Locatie</Label>
            <Input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="ex: RAR Sector 4, Service Scutersh..." data-testid="input-location" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Nume client</Label>
              <Input value={form.client_name} onChange={e => setForm(p => ({ ...p, client_name: e.target.value }))} placeholder="Ion Popescu" />
            </div>
            <div className="space-y-2">
              <Label>Telefon client</Label>
              <Input value={form.client_phone} onChange={e => setForm(p => ({ ...p, client_phone: e.target.value }))} placeholder="+40721..." />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Observatii..." />
          </div>
          <Button onClick={handleSubmit} disabled={loading} className="w-full" data-testid="submit-programare">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
            {loading ? "Se salveaza..." : "Creeaza programare"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ProgramariPage() {
  const [programari, setProgramari] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [scooters, setScooters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState("list");

  const fetchData = async () => {
    setLoading(true);
    try {
      const [progResp, upResp, scResp] = await Promise.all([
        api.get(`/programari?prog_type=${filterType}&status=${filterStatus}`),
        api.get("/programari/upcoming"),
        api.get("/scooters?limit=500"),
      ]);
      setProgramari(progResp.data);
      setUpcoming(upResp.data);
      setScooters(scResp.data || []);
    } catch (err) {
      toast.error("Eroare la incarcarea datelor");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [filterType, filterStatus]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/programari/${id}`, { status });
      toast.success(`Status: ${STATUS_CONFIG[status]?.label}`);
      fetchData();
    } catch (err) { toast.error("Eroare"); }
  };

  const deleteProgramare = async (id) => {
    try {
      await api.delete(`/programari/${id}`);
      toast.success("Stearsa");
      fetchData();
    } catch (err) { toast.error("Eroare"); }
  };

  const filtered = useMemo(() => {
    if (!search) return programari;
    const q = search.toLowerCase();
    return programari.filter(p =>
      (p.scooter_label || "").toLowerCase().includes(q) ||
      (p.client_name || "").toLowerCase().includes(q) ||
      (p.location || "").toLowerCase().includes(q) ||
      (p.scooter_id || "").toLowerCase().includes(q)
    );
  }, [programari, search]);

  const stats = {
    total: programari.length,
    programat: programari.filter(p => p.status === "programat").length,
    upcoming: upcoming.length,
    today: programari.filter(p => getDaysUntil(p.scheduled_date) === 0 && p.status === "programat").length,
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 space-y-6" data-testid="programari-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              Programari
            </h1>
            <p className="text-sm text-muted-foreground">Gestionare programari RAR, revizii, reparatii si livrari</p>
          </div>
          <CreateProgramareDialog scooters={scooters} onCreated={fetchData} />
        </div>

        {/* Upcoming Alerts */}
        {upcoming.length > 0 && (
          <div className="space-y-2" data-testid="upcoming-alerts">
            {upcoming.map(u => {
              const cfg = TYPE_CONFIG[u.programare_type] || TYPE_CONFIG.revizie;
              const days = u.days_until ?? getDaysUntil(u.scheduled_date);
              return (
                <div key={u.programare_id} className={`flex items-center gap-3 rounded-lg p-3 border ${days === 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-yellow-500/10 border-yellow-500/30'}`}>
                  <Bell className={`w-5 h-5 flex-shrink-0 ${days === 0 ? 'text-red-500' : 'text-yellow-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      <Badge className={`${cfg.color} text-white`}>{cfg.label}</Badge>
                      <span>{u.scooter_label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {days === 0 ? 'ASTAZI' : `In ${days} ${days === 1 ? 'zi' : 'zile'}`} &bull; {u.scheduled_time || '--:--'} &bull; {u.location || 'Locatie nespecificata'}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => updateStatus(u.programare_id, "verificat")} data-testid={`verify-${u.programare_id}`}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Verificat
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-border"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent></Card>
          <Card className="border-yellow-500/30"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono text-yellow-500">{stats.programat}</p>
            <p className="text-xs text-muted-foreground">Programate</p>
          </CardContent></Card>
          <Card className="border-red-500/30"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono text-red-500">{stats.upcoming}</p>
            <p className="text-xs text-muted-foreground">Alerte active</p>
          </CardContent></Card>
          <Card className="border-orange-500/30"><CardContent className="p-3 text-center">
            <p className="text-2xl font-bold font-mono text-orange-500">{stats.today}</p>
            <p className="text-xs text-muted-foreground">Astazi</p>
          </CardContent></Card>
        </div>

        {/* Filters + View Toggle */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 bg-secondary/50 rounded-lg px-3 py-1.5 flex-1 max-w-sm">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cauta scuter, client..." className="bg-transparent text-sm outline-none flex-1" data-testid="search-programari" />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder="Tip" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate tipurile</SelectItem>
              {Object.entries(TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toate</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-lg overflow-hidden border border-border">
            <button onClick={() => setViewMode("list")} className={`px-3 py-1.5 text-xs font-medium ${viewMode === "list" ? "bg-primary text-primary-foreground" : "bg-secondary/50"}`}>Lista</button>
            <button onClick={() => setViewMode("calendar")} className={`px-3 py-1.5 text-xs font-medium ${viewMode === "calendar" ? "bg-primary text-primary-foreground" : "bg-secondary/50"}`} data-testid="calendar-toggle">Calendar</button>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" /></div>
        ) : viewMode === "calendar" ? (
          <CalendarView programari={filtered} currentMonth={currentMonth} setCurrentMonth={setCurrentMonth} />
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground"><Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Nicio programare</p></div>
        ) : (
          <div className="space-y-3">
            {filtered.map(item => {
              const cfg = TYPE_CONFIG[item.programare_type] || TYPE_CONFIG.revizie;
              const stCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.programat;
              const Icon = cfg.icon;
              const days = getDaysUntil(item.scheduled_date);
              return (
                <Card key={item.programare_id} className="border-border" data-testid={`programare-${item.programare_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex gap-3 flex-1 min-w-[250px]">
                        <div className={`w-10 h-10 rounded-lg ${cfg.color} flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-bold text-sm">{cfg.label}</span>
                            <Badge className={stCfg.color}>{stCfg.label}</Badge>
                            {days !== null && days >= 0 && days <= 3 && item.status === "programat" && (
                              <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {days === 0 ? 'ASTAZI' : `In ${days}z`}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm">{item.scooter_label || item.scooter_id}</p>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1 flex-wrap">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{item.scheduled_date}</span>
                            {item.scheduled_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{item.scheduled_time}</span>}
                            {item.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{item.location}</span>}
                            {item.client_name && <span className="flex items-center gap-1"><User className="w-3 h-3" />{item.client_name}</span>}
                            {item.client_phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{item.client_phone}</span>}
                          </div>
                          {item.notes && <p className="text-xs text-muted-foreground mt-1 italic">{item.notes}</p>}
                          {item.verified_by && <p className="text-xs text-green-500 mt-1">Verificat de: {item.verified_by} ({item.verified_at ? new Date(item.verified_at).toLocaleDateString("ro-RO") : ''})</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {item.status === "programat" && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => updateStatus(item.programare_id, "verificat")}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Verificat
                            </Button>
                            <Button size="sm" variant="outline" className="border-green-500 text-green-500" onClick={() => updateStatus(item.programare_id, "finalizat")}>
                              Finalizat
                            </Button>
                            <Button size="sm" variant="outline" className="border-red-500 text-red-500" onClick={() => updateStatus(item.programare_id, "anulat")}>
                              Anulat
                            </Button>
                          </>
                        )}
                        {item.status === "verificat" && (
                          <Button size="sm" variant="outline" className="border-green-500 text-green-500" onClick={() => updateStatus(item.programare_id, "finalizat")}>
                            <CheckCircle className="w-3 h-3 mr-1" /> Finalizat
                          </Button>
                        )}
                        {(item.status === "finalizat" || item.status === "anulat") && (
                          <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => deleteProgramare(item.programare_id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
